import { Button, Callout, Field, Input } from "@gf/ui";
import { MailCheck } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { signupApi } from "./api";
import { errorText } from "./flow";

const RESEND_COOLDOWN_MS = 30_000;

/** Step 2: the link is on its way. Resend it, or go back and fix the address. */
export function CheckEmailStep({ email, onChangeEmail }: { email: string; onChangeEmail: () => void }) {
  const [busy, setBusy] = useState(false);
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const coolingDown = sentAt !== null;

  useEffect(() => {
    if (sentAt === null) return;
    const t = window.setTimeout(() => setSentAt(null), RESEND_COOLDOWN_MS);
    return () => window.clearTimeout(t);
  }, [sentAt]);

  async function resend() {
    setBusy(true);
    setError(null);
    try {
      await signupApi.recover(email);
      setSentAt(Date.now());
    } catch (err) {
      setError(errorText(err, "We couldn't send a new link. Try again in a minute."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="gf-stack">
      <div className="signup-mail">
        <span className="gfp-auth__icon" aria-hidden="true">
          <MailCheck size={22} />
        </span>
        <ol className="signup-mail__list">
          <li>
            <strong>Open the email from GramFather</strong> (noreply@gramfather.com) sent to <strong>{email}</strong>. Not there in a minute? Check
            spam and promotions.
          </li>
          <li>
            <strong>Click the link</strong> in it. It brings you right back here.
          </li>
          <li>
            <strong>Choose a password and add your card.</strong> Then you're in.
          </li>
        </ol>
      </div>
      {coolingDown ? <Callout tone="success">New link sent to {email}. Use the newest email.</Callout> : null}
      {error ? <Callout tone="danger">{error}</Callout> : null}
      <div className="signup-actions">
        <Button variant="secondary" onClick={() => void resend()} loading={busy} disabled={coolingDown}>
          {coolingDown ? "Link sent" : "Resend the email"}
        </Button>
        <Button variant="ghost" onClick={onChangeEmail} disabled={busy}>
          Use a different email
        </Button>
      </div>
    </div>
  );
}

/** Step 2 again: no session in this tab (expired link, another device). Send a fresh link. */
export function RecoverStep({ initialEmail, onSent }: { initialEmail: string; onSent: (email: string) => void }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(address)) return setError("Enter the email you signed up with.");
    setBusy(true);
    setError(null);
    try {
      await signupApi.recover(address);
      onSent(address);
    } catch (err) {
      setError(errorText(err, "We couldn't send a new link. Try again in a minute."));
      setBusy(false);
    }
  }

  return (
    <form className="gfp-auth__form" onSubmit={onSubmit} noValidate>
      <Field label="Your signup email">
        <Input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={254}
          required
          size="lg"
          autoFocus={!initialEmail}
        />
      </Field>
      {error ? <Callout tone="danger">{error}</Callout> : null}
      <Button type="submit" variant="primary" size="lg" block loading={busy}>
        Send a new link
      </Button>
    </form>
  );
}
